import React, { useMemo } from 'react';
import {
  CalendarOutlined,
  DollarCircleOutlined,
  HistoryOutlined,
  SyncOutlined,
  FieldNumberOutlined,
  DiffOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Button, Col, Row, Tooltip } from 'antd';
import StatCard from '@components/StatCard';
import { useTranslation } from 'react-i18next';

interface InvoiceStatsProps {
  data: {
    value: string | 0;
    expirationDate: string;
    totalTraffic: string | 0;
    period: string;
    number: string;
    terminals?: string | number;
    chips?: string;
  };
}

const InvoiceStats: React.FC<InvoiceStatsProps> = ({ data }) => {
  const { t } = useTranslation('invoices');

  const treatedNumber = useMemo(() => {
    const num = data?.number?.replaceAll(' ', '');

    return num === '' ? '-' : num;
  }, [data?.number]);

  const allStats = useMemo(
    () => [
      {
        name: 'number',
        icon: <FieldNumberOutlined />,
        value: treatedNumber,
        hasInfo: treatedNumber !== '-',
      },
      {
        name: 'totalTraffic',
        icon: <SyncOutlined />,
        value: data?.totalTraffic,
        hasInfo: false,
      },
      {
        name: 'period',
        icon: <HistoryOutlined />,
        value: data?.period,
        hasInfo: false,
      },
      {
        name: 'value',
        icon: <DollarCircleOutlined />,
        value: data?.value,
        hasInfo: false,
      },
      {
        name: 'chips',
        icon: <DiffOutlined />,
        value: data?.chips,
        hasInfo: false,
      },
      {
        name: 'expirationDate',
        icon: <CalendarOutlined />,
        value: data?.expirationDate,
        hasInfo: false,
      },
    ],
    [data, treatedNumber],
  );

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      {allStats?.map(({ name, icon, value, hasInfo }) => (
        <Col key={name} lg={12} md={12} xl={8} xs={24}>
          <StatCard
            key={name}
            title={
              <>
                {t(`invoice.${name}`)}
                {hasInfo && (
                  <Tooltip title={t(`detailed.invoice.numberHelper`)}>
                    <Button
                      type="link"
                      size="small"
                      icon={<InfoCircleOutlined />}
                    />
                  </Tooltip>
                )}
              </>
            }
            icon={icon}
            value={value}
          />
        </Col>
      ))}
    </Row>
  );
};

export default InvoiceStats;
